/**
 * The single money formatter for the app (PROD-1804).
 *
 * Before this existed, React formatted money two different ways and disagreed
 * with itself on the same screen flow:
 *
 *   src/patients/components/billing/**   .toFixed(2)      -> "176925.00"
 *   src/billing/**                        toLocaleString   -> "176,925.00"
 *
 * `available-balance-card.tsx` had already diagnosed exactly this and deferred
 * the fix, noting that converging "needs one shared money formatter (none
 * exists anywhere in `src/`) and is its own ticket". This module is that
 * formatter.
 *
 * ## Why ungrouped is the default
 *
 * Flutter formats every billing amount with bare `toStringAsFixed(2)` —
 * `invoice_main_model.dart:440-468` covers grossTotal, netTotal, discountTotal,
 * patientPayable, managementDiscount, deposited, patientOutstanding and
 * sponsorOutstanding. So ungrouped is the Flutter-correct side of React's
 * internal disagreement, and aligning to it is a pure parity fix that needs no
 * product sign-off.
 *
 * Making React MORE readable than Flutter (grouping everywhere) would be a
 * deliberate divergence, which needs a manifest entry and a product decision —
 * deliberately not taken here. `AED 176925.00` is admittedly easy to misread,
 * and that argument is recorded on PROD-1804 for whoever owns that call. If it
 * is later approved, this is now the one file to change.
 *
 * ## The reconciliation carve-out is real, not an oversight
 *
 * `formatMoneyGrouped` exists because reconciliation is the one surface where
 * BOTH apps group digits: Flutter uses
 * `NumberFormat("#,##0.00", "en_US")` at `claim_reconciliation_screen.dart:189`.
 * Converging reconciliation to the ungrouped default would CREATE a divergence
 * rather than remove one. Do not "clean this up".
 *
 * ## The canonical anti-regression test
 *
 * `src/patients/components/billing/available-balance-card.test.tsx:128-135` is
 * the load-bearing guard for this module's default. It asserts a rendered
 * balance is `AED 176780.00` and specifically NOT `176,780`, with the comment
 * "Guards against a drive-by switch to toLocaleString". It predates this module
 * and it still passes unchanged — the ungrouped direction was chosen partly so
 * that guard stays meaningful rather than needing deletion.
 *
 * **Do not remove it in a cleanup pass.** If money formatting is ever converged
 * onto grouping by product decision, that test is the thing to update
 * deliberately, and updating it is the signal that a divergence from Flutter is
 * being accepted on purpose.
 */

/**
 * Coerce to a finite number for display — null/undefined AND NaN/Infinity all
 * collapse to 0, so a bad amount renders "0.00" rather than "NaN"/"Infinity".
 * (`??` alone would let `NaN` through, since NaN is not null.)
 */
function finite(n?: number | null): number {
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/**
 * Standard money display: always 2 decimals, no thousands separator.
 * Mirrors Flutter's `toStringAsFixed(2)`.
 *
 *   formatMoney(176925)   -> "176925.00"
 *   formatMoney(0)        -> "0.00"
 *   formatMoney(null)     -> "0.00"
 *   formatMoney(NaN)      -> "0.00"
 */
export function formatMoney(n?: number | null): string {
  return finite(n).toFixed(2);
}

/**
 * Terse money display: 2 decimals, but a whole amount drops the ".00".
 * Used by list/table columns where the decimals are noise at a glance
 * (claim workbench, cashier shift, provider fees).
 *
 *   formatMoneyTerse(300)     -> "300"
 *   formatMoneyTerse(300.5)   -> "300.50"
 *   formatMoneyTerse(176925)  -> "176925"
 */
export function formatMoneyTerse(n?: number | null): string {
  return formatMoney(n).replace(/\.00$/, '');
}

/**
 * Grouped money display — **reconciliation only. Do not "clean this up"
 * to match `formatMoney`. See PROD-1804.**
 *
 * Reconciliation is the single surface where BOTH apps group digits: Flutter
 * uses `NumberFormat("#,##0.00", "en_US")` at
 * `claim_reconciliation_screen.dart:189`. Every other money surface in both
 * apps is ungrouped. So converging this one onto `formatMoney` would CREATE a
 * Flutter divergence rather than remove one — the exact opposite of what
 * PROD-1804 set out to do.
 *
 * The locale is pinned to `en-US` rather than left `undefined`: with
 * `undefined` the output follows the viewer's browser locale, so the same
 * figure renders `176,925.00` for one member of staff and `176.925,00` for
 * another. Not acceptable on a financial reconciliation screen.
 *
 *   formatMoneyGrouped(176925) -> "176,925.00"
 */
export function formatMoneyGrouped(n?: number | null): string {
  return finite(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
